import {
  AnalyticsProps,
  NOT_TRACKED_CONTENT,
} from '../../InternalLibrary/analytics';
import {
  default as FieldGroup,
  FieldGroupProps,
} from 'components/atoms/FieldGroup/FieldGroup';
import { default as Input, InputProps } from 'components/atoms/Input/Input';
import { useAnalytics } from 'use-analytics';
import { useCallback, useRef } from 'react';
import { useFormik } from 'formik';
import { useValidationText } from 'hooks/useValidationText';

export interface InputActorProps extends AnalyticsProps {
  className?: string;
  formik: ReturnType<typeof useFormik<any>>;
  name: string;
  label: string;
  fieldGroupProps?: Omit<FieldGroupProps, 'children'>;
  inputProps?: Partial<InputProps>;
  sanitizer?: (value: string) => string;
  formatter?: (value: string) => string;
}

export const InputActor: React.FC<InputActorProps> = ({
  className,
  formik,
  name,
  label,
  fieldGroupProps = {},
  inputProps = {},
  sanitizer,
  formatter,
  analytics,
}) => {
  const valueRef = useRef(formik.values[name]);
  const { track } = useAnalytics();

  const onChange = useCallback(
    (e: any) => {
      formik.status?.errors?.[name] && delete formik.status.errors[name];
      formik.handleChange(e);

      if (sanitizer) {
        formik.setFieldValue(name, sanitizer(e.target.value));
      }
      if (analytics?.dontTrack) {
        return;
      }
      const isPasted =
        e.target.value.length - (valueRef.current as string).length > 1;
      valueRef.current = e.target.value;
      track(
        'input',
        {
          description: analytics?.description,
          inputPlaceholder: label,
          value: analytics?.dontTrackContent
            ? NOT_TRACKED_CONTENT
            : analytics?.contentOverride ?? e.target.value,
        },
        {
          isPasted,
        },
      );

      if (inputProps?.onChange) {
        inputProps?.onChange(e);
      }
    },
    [
      analytics?.contentOverride,
      analytics?.description,
      analytics?.dontTrack,
      analytics?.dontTrackContent,
      formik,
      label,
      name,
      sanitizer,
      track,
    ],
  );

  const validationErrorText = useValidationText(formik, name);

  return (
    <FieldGroup
      className={className}
      validationText={validationErrorText}
      {...fieldGroupProps}
    >
      <Input
        {...inputProps}
        dataTest={`input-${name}`}
        label={label}
        name={name}
        onChange={onChange}
        error={validationErrorText !== undefined}
        onBlur={formik.handleBlur}
        value={formik.values[name]}
        formatter={formatter}
      />
    </FieldGroup>
  );
};
